import { ChangeDetectorRef, Component, inject, OnDestroy } from '@angular/core'
import { ModalHeaderComponent } from '../modal-header/modal-header.component'
import { ModalFooterComponent } from '../modal-footer/modal-footer.component'
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap'
import { ModalBodyComponent } from '../modal-body/modal-body.component'
import { firstValueFrom, Subscription, switchMap, take, tap } from 'rxjs'
import { FieldColumnsComponent } from '../../field-columns/field-columns.component'
import { LabeledPropertyComponent } from '../../labeled-property/labeled-property.component'
import {
  ServicePointManagementService,
} from '../../service-point-management/service-point-management.service'
import { ServicePointFilter } from 'src/app/shared/services/filter/service-point-filter'
import { ServicePointEffectiveDatedAttribute } from '../../service-point-management/model'
import { Filter, FilterService } from 'src/app/shared/services/filter/filter.service'
import { ReferenceDataService } from '../../reference-data/reference-data.service'
import { DateRangeFilter } from 'src/app/shared/services/filter/date-range-filter'
import { dateTimeFormat } from 'src/app/shared/utilities/date'
import { DateRangeFilterPickerComponent } from '../../date-range-filter-picker/date-range-filter-picker.component'
import { ServicePointSelectorComponent } from '../../service-point-selector/service-point-selector.component'

@Component({
  imports: [
    ModalHeaderComponent,
    ModalBodyComponent,
    ModalFooterComponent,
    FieldColumnsComponent,
    LabeledPropertyComponent,
    DateRangeFilterPickerComponent,
    ServicePointSelectorComponent,
  ],
  selector: 'app-create-service-point-flag-modal',
  styles: `
  .only-md {
    display: none;
    @media (min-width: 768px) and (max-width: 991px) {
      display: block;
    }
  }`,
  template: `
    <div app-modal-header>Delete Service Point Flag</div>
    <div app-modal-body>
      <div app-field-columns>
@if(servicePoint('service_point_uid')){
        <div app-labeled-property label="Service Point">
          <div
            app-service-point-selector
            [selectedServicePoints]="servicePoint('service_point_uid').servicePoints"
            [slim]="true"
            [config]="servicePoint('service_point_uid').config"
            [allowPartial]="false"
            [disabled]="true"
          ></div>
        </div>
        }

        <br class="only-md"/>
        @if(dateRange('effective_date')){
        <div app-labeled-property label="Effective Date">
          <div dateRangeFilterPicker
            [value]="dateRange('effective_date')"
            [showOperation]="dateRange('effective_date').config.showOperation ?? false"
            [showRange]="dateRange('effective_date').config.showRange ?? false"
            [showTime]="dateRange('effective_date').config.showTime ?? false"
            [showSeconds]="dateRange('effective_date').config.showSeconds ?? false"
            [disabled]=true
          ></div>
        </div>
        }
        @if(dateRange('termination_date')){
        <div app-labeled-property label="Termination Date">
          <div dateRangeFilterPicker
            [value]="dateRange('termination_date')"
            [showOperation]="dateRange('termination_date').config.showOperation ?? false"
            [showRange]="dateRange('termination_date').config.showRange ?? false"
            [showTime]="dateRange('termination_date').config.showTime ?? false"
            [showSeconds]="dateRange('termination_date').config.showSeconds ?? false"
            [disabled]=true
          ></div>
        </div>
        }
      </div>
    </div>
    <div app-modal-footer>
      <button class="action-btn danger" (click)="onDelete()">Confirm</button>
      <button class="action-btn" (click)="onCancel()">Close</button>
    </div>
  `,
})
export class DeleteServicePointFlagModalComponent implements OnDestroy {
  activeModal = inject(NgbActiveModal)
  referenceDataService = inject(ReferenceDataService)
  filterService = inject(FilterService)
  changeDetectorRef = inject(ChangeDetectorRef)
  servicePointManagementService = inject(ServicePointManagementService)

  private subscription = new Subscription()
  private onSuccess: () => void = () => void 0
  public data: ServicePointEffectiveDatedAttribute
  public format = dateTimeFormat
  public state: Record<string, Filter> = {}

  async init(
    data: ServicePointEffectiveDatedAttribute,
    onSuccess: () => void = () => void 0,
  ) {
    this.onSuccess = onSuccess
    this.data = data

    const { dispatch } = this.filterService
    dispatch(FilterService.InitFiltersAction({
      fieldGroup: DeleteServicePointFlagModalComponent.name,
      fields: {
        'service_point_uid': new ServicePointFilter({
          servicePoints: [{
            service_point_uid:data.service_point_uid, 
            service_point_id: data.service_point_id,
          }],
          config: {
            constant: true,
            meta: {
              fieldGroup: DeleteServicePointFlagModalComponent.name,
              fieldName: 'service_point_uid',
              fieldDisplay: 'Service Point',
            },
          },
        }, dispatch),
        'effective_date': new DateRangeFilter({
          value: data.effective_date,
          operation: 'eq',
          config: {
            constant: true,
            meta: {
              fieldGroup: DeleteServicePointFlagModalComponent.name,
              fieldName: 'effective_date',
              fieldDisplay: 'Effective Date',
            },
          },
        }, dispatch),
        'termination_date': new DateRangeFilter({
          value: data.termination_date,
          operation: 'eq',
          config: {
            constant: true,
            meta: {
              fieldGroup: DeleteServicePointFlagModalComponent.name,
              fieldName: 'termination_date',
              fieldDisplay: 'Termination Date',
            },
          },
        }, dispatch),
      },
    }))
    this.subscription.add(this.filterService.state(DeleteServicePointFlagModalComponent.name).subscribe(state => this.state = state))
    this.changeDetectorRef.detectChanges()
  }
  
  dateRange(key: string) {
    return (this.state[key] as DateRangeFilter)?.state
  }

  servicePoint(key: string) {
    return (this.state[key] as ServicePointFilter)?.state
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe()
  }

  async onDelete() {
    await firstValueFrom(
      this.filterService.state(DeleteServicePointFlagModalComponent.name).pipe(
        take(1),
        switchMap((state) => {
          const servicePoint = (
            state['service_point_uid'] as ServicePointFilter
          ).state.servicePoints[0].service_point_id
          const effectiveDate = (state['effective_date'] as DateRangeFilter).state.value as Date
          return this.servicePointManagementService.deleteFlag(
            servicePoint,
            effectiveDate,
          )
        }),
        tap(() => {
          this.onSuccess()
          this.activeModal.dismiss()
        }),
      ),
    )
  }

  onCancel() {
    this.activeModal.dismiss()
  }
}
